import { forEach, get, groupBy, mapValues, orderBy, set } from 'lodash';
import {
  activityCategoryDefaultDates,
  activityTypes,
  protectionApplicationTypes
} from 'constants/activities';
import { scriptTypes } from 'constants/scripts';
import {
  formatDateHyphenated,
  formatDateToISO8601
} from 'util/formatters/dateFormatter';

const orderFertilityActivities = (byActivityType) => {
  return [
    get(byActivityType, activityTypes.FERTILITY_FALL),
    get(byActivityType, activityTypes.FERTILITY_PRE_PLANT),
    get(byActivityType, activityTypes.FERTILITY_IN_SEASON),
    get(byActivityType, activityTypes.FERTILITY_LATE_SEASON),
    get(byActivityType, activityTypes.FERTILITY_OTHER)
  ];
};

const orderApplicationActivities = (byActivityType) => {
  return [
    get(byActivityType, protectionApplicationTypes.APPLICATION_FUNGICIDE),
    get(byActivityType, protectionApplicationTypes.APPLICATION_HERBICIDE),
    get(byActivityType, protectionApplicationTypes.APPLICATION_INSECTICIDE),
    get(byActivityType, protectionApplicationTypes.APPLICATION_GROWTHREGULATOR),
    get(byActivityType, protectionApplicationTypes.APPLICATION_MIX)
  ];
};
const activities = [activityTypes.PLANTING];
Object.values(activityTypes).forEach((val) => activities.push(val));
Object.values(protectionApplicationTypes).forEach((val) =>
  activities.push(val)
);

export const parseActivities = (rawActivities) => {
  let { result } = rawActivities;
  const filtered = result.filter((res) => activities.includes(res.type));
  result = orderBy(filtered, ['date'], ['desc']); // Order ensures category will use most recent - planting needs this
  // current assumption -- 1 activity type / field / cropping year (TODO: revisit for season when multiple seasons/year)
  let parsed = {
    byActivityType: mapValues(
      groupBy(result, (activity) =>
        activity.type.startsWith(activityTypes.application)
          ? 'application'
          : activity.type
      ),
      (activities) => activities[0]
    ),
    byDate: mapValues(
      groupBy(result, (activity) => formatDateHyphenated(activity.date)),
      (activities) => activities[0]
    ),
    byUuid: mapValues(
      groupBy(result, (activity) => activity.uuid),
      (activities) => activities[0]
    )
  };
  parsed.ordered = orderFertilityActivities(parsed.byActivityType);

  return parsed;
};

export const parseActivity = (rawActivity) => {
  return get(rawActivity, 'activities[0]');
};

export const parseActivityCategoriesV3 = (rawCategories) => {
  // Wrote this then found out we AREN'T supposed to use v3 yet
  // Keeping for when we do.
  const parsed = {
    byActivityType: {},
    byUuid: {},
    ordered: {}
  };

  // Data format is weird.  In v3 api:
  // an object of country/data key/value pairs.
  // Each country holds an _array_
  // Each element of this array will be an object with a _single_ key, an activity category
  // Each value of an activity is an array of timing objects, where the array indicates the order
  // TODO: Adjust for by-country breakdown once we have another country to test
  get(rawCategories, 'result.US').forEach((category) => {
    Object.entries(category).forEach(([label, info]) => {
      info.forEach((rawActivity, index) => {
        const activityType = `${label}-${rawActivity.code}`; // TODO: Why hyphenated?
        const activity = {
          activityCategoryUuid: rawActivity.uuid,
          code: rawActivity.code,
          activityType,
          label,
          index
        };
        set(parsed, `byActivityType.${activityType}`, activity);
        set(parsed, `byUuid.${rawActivity.uuid}`, activity);
        set(parsed, `ordered[${label}][${index}]`, activity);
      });
    });
  });
  // planting isn't sent back by groot, we manually tack it on
  set(parsed, `byActivityType.planting`, {
    // planting has no category uuid
    code: 'planting',
    activityCategoryUuid: 'planting',
    activityType: 'planting',
    index: 0,
    label: 'planting'
  });

  return parsed;
};
export const parseActivityCategories = (rawCategories) => {
  let parsed = {};
  const categories = get(rawCategories, 'result.US[0]'); // TODO: i18n: make this work for other countries. how does groot determine country?
  categories.planting = {
    planting: {
      // planting has no category uuid
      code: 'planting',
      'category-name': 'Planting'
    }
  };

  // TODO: Decide on how we want to get this category in - possibly just via that rawCategories call?
  const today = new Date();
  // Replace current time with offset
  const defaultDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).toISOString();

  categories.application = {
    application: {
      activityType: 'application',
      code: 'application',
      'category-name': 'Application',
      activityCategoryUuid: '053d6473-da43-4878-aa5a-d45e299eabd4',
      date: defaultDate
    },
    fungicide: {
      activityType: 'application-fungicide',
      code: 'application-fungicide',
      'category-name': 'Application Fungicide',
      activityCategoryUuid: 'fungicide',
      date: defaultDate
    },
    herbicide: {
      activityType: 'application-herbicide',
      code: 'application-herbicide',
      'category-name': 'Application Herbicide',
      activityCategoryUuid: 'herbicide',
      date: defaultDate
    },
    insecticide: {
      activityType: 'application-insecticide',
      code: 'application-insecticide',
      'category-name': 'Application Insecticide',
      activityCategoryUuid: 'insecticide',
      date: defaultDate
    },
    growthregulator: {
      activityType: 'application-growthregulator',
      code: 'application-growthregulator',
      'category-name': 'Growth Regulator',
      activityCategoryUuid: 'growthregulator',
      date: defaultDate
    },
    mix: {
      activityType: 'application-mix',
      code: 'application-mix',
      'category-name': 'Mix',
      activityCategoryUuid: 'mix',
      date: defaultDate
    }
  };

  Object.keys(categories).map((scriptType) => {
    const categoriesForScriptType = categories[scriptType];

    Object.keys(categoriesForScriptType).map((activityCategoryUuid) => {
      const category = categoriesForScriptType[activityCategoryUuid];
      const { code } = category;
      const activityType
        = scriptType === scriptTypes.FERTILITY ? `${scriptType}-${code}` : code;
      const activityCategory = {
        ...category,
        activityType,
        activityCategoryUuid
      };

      set(parsed, `byActivityType.${activityType}`, activityCategory);
      set(parsed, `byUuid.${activityCategoryUuid}`, activityCategory);
      return null;
    });
    parsed.ordered = {
      fertility: orderFertilityActivities(parsed.byActivityType),
      application: orderApplicationActivities(parsed.byActivityType)
    };
    return null;
  });
  return parsed;
};

export const parseActivityDefaultDates = ({
  [scriptTypes.FERTILITY]: fertilityProductYear,
  [scriptTypes.PLANTING]: plantingProductYear
}) => {
  return mapValues(activityCategoryDefaultDates, (value, key) => {
    let dateStringYear
      = key === activityTypes.PLANTING
        ? plantingProductYear
        : fertilityProductYear;
    // fall fertility application means the product year's previous fall,
    // so subtract 1 from the product year
    if (key === activityTypes.FERTILITY_FALL) {
      dateStringYear -= 1;
    }
    const dateString = `${dateStringYear}-${value}`;
    return formatDateToISO8601(dateString);
  });
};

export const parseActivityCategoriesWithDefaultDates = ({
  categories,
  activityDefaultDates
}) => {
  return forEach(categories, (categories) => {
    return forEach(categories, (cat) => {
      if (Array.isArray(cat)) {
        // ordered prop is an array
        cat.forEach((activity) => {
          activity.date
            = activity.date || activityDefaultDates[activity.activityType];
        });
      } else {
        cat.date = cat.date || activityDefaultDates[cat.activityType];
      }
    });
  });
};
